import { createAsyncThunk, createAction } from '@reduxjs/toolkit';
import {
  getCandidates,
  searchCandidates,
  fetchCandidateById,
  getTagsOptions,
  updateCandidateChannel,
  getMessageTemplates,
  getICIMSCandidateData,
  addCandidate as addCandidateApi,
  updateCandidate as updateCandidateApi,
  getCandidateScheduledMessages,
  getCandidatesCampaignById,
  uploadCandidates as uploadCandidatesApi,
  starCandidateApi,
  inactiveCandidateApi,
  archiveCandidateApi,
  updateAllActiveCandidateApi,
  updateDoNotTextCandidateApi,
  getCandidatesMetricsById,
  removeCandidateTag,
  addCandidateTag,
  validateCandidate as validateCandidateApi,
  candidateQueryWithTags,
  removeSelectedCampaignsApi,
  candidatesFileUpload as candidatesFileUploadApi,
  fetchOmniSearch as fetchOmniSearchApi,
  purgeCandidates,
  cancelScheduledMessages as cancelScheduledMessagesApi,
  getATSCandidatesAPI,
  getaddATSCandidate,
} from 'util/Api/user';
import { fetchCandidatesInSequence as fetchCandidatesInSequenceApi } from 'util/Api/customer';
import { CandidateSource } from 'appConstants';

export const updateDoNotTextFlag = createAction('thread/updateDoNotTextFlag');
export const resetImportModalSuccessFlag = createAction('thread/resetImportModalSuccessFlag');
export const resetCandidateUpdatedFlag = createAction('thread/resetCandidateUpdatedFlag');
export const resetIsCancelMessageSuccess = createAction('candidate/resetIsCancelMessageSuccess');
export const addAtsCandidateSuccess = createAction('candidate/addAtsCandidateSuccess');
export const updateAtsCandidateSavedStatus = createAction('candidate/updateAtsCandidate');
export const setCandidateSaved = createAction('candidate/setCandidateSaved');
export const resetIsError = createAction('candidate/candidatesImport');
export const resetCandidateUpdated = createAction('candidate/resetCandidateUpdated');
export const resetCandidateOptions = createAction('candidate/resetCandidateOptions');
export const resetTagsOptions = createAction('candidate/resetTagsOptions');

export const fetchCandidates = createAsyncThunk(
  'candidate/fetchCandidates',
  async (
    { pageNumber, itemsPerPage, sort, tagId = null, filter, status = null },
    { rejectWithValue },
  ) => {
    try {
      return await getCandidates({
        pageNumber,
        itemsPerPage,
        sort,
        tagId,
        filter,
        status,
      });
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const fetchATSCandidates = createAsyncThunk(
  'candidate/fetchATSCandidates',
  async ({ pageNumber, itemsPerPage, sort, searchValue }, { rejectWithValue }) => {
    try {
      return await getATSCandidatesAPI({
        pageNumber,
        itemsPerPage,
        sort,
        searchValue,
      });
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const addATSCandidate = createAsyncThunk(
  'candidate/addATSCandidate',
  async ({ candidatesIds }, { rejectWithValue }) => {
    try {
      return await getaddATSCandidate({ candidatesIds });
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const searchCandidateOptions = createAsyncThunk(
  'candidate/searchCandidateOptions',
  async ({ pageNumber, value }, { rejectWithValue }) => {
    try {
      return await searchCandidates({ pageNumber, value });
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const fetchTagsOptions = createAsyncThunk(
  'candidate/fetchTagsOptions',
  async ({ pageNumber, searchValue }, { rejectWithValue }) => {
    try {
      return await getTagsOptions({ pageNumber, searchValue });
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const fetchById = createAsyncThunk(
  'candidate/fetchById',
  async ({ id }, { rejectWithValue }) => {
    try {
      return await fetchCandidateById({ id });
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const deleteCandidatesAction = createAsyncThunk(
  'candidate/deleteCandidates',
  async ({ candidatesIds }, { rejectWithValue }) => {
    try {
      return await purgeCandidates({ candidatesIds });
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const fetchCandidatesCampaignById = createAsyncThunk(
  'candidate/fetchCandidatesCampaignById',
  async ({ candidateId }, { rejectWithValue }) => {
    try {
      return await getCandidatesCampaignById({
        candidateId,
      });
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const fetchCandidateMetricsById = createAsyncThunk(
  'candidate/fetchCandidateMetricsById',
  async ({ candidateId }, { rejectWithValue }) => {
    try {
      return await getCandidatesMetricsById({
        candidateId,
      });
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const deleteCandidateTag = createAsyncThunk(
  'candidate/deleteCandidateTag',
  async ({ candidateId, tagName }, { rejectWithValue }) => {
    try {
      return await removeCandidateTag({
        candidateId,
        tagName,
      });
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const addTagForCandidate = createAsyncThunk(
  'candidate/addTagForCandidate',
  async ({ candidateId, tagName }, { rejectWithValue }) => {
    try {
      return await addCandidateTag({
        candidateId,
        tagName,
      });
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const updateChannel = createAsyncThunk(
  'candidate/updateChannel',
  async ({ candidateId, channel }, { rejectWithValue }) => {
    try {
      return await updateCandidateChannel({ candidateId, channel });
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const fetchICIMSData = createAsyncThunk(
  'candidate/fetchICIMSData',
  async ({ messageTemplates, candidateId }, { rejectWithValue }) => {
    try {
      let shouldRetrieveICIMSData = false;

      // If any of the message templates have icims-placeholder we need to retrieve corresponding data
      messageTemplates.forEach((template) => {
        // eslint-disable-next-line no-implicit-coercion, no-bitwise
        if (~template.text.indexOf(`{{${CandidateSource.ICIMS}`)) {
          shouldRetrieveICIMSData = true;
        }
      });

      if (shouldRetrieveICIMSData) {
        return await getICIMSCandidateData(candidateId);
      }

      return null;
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const fetchCandidateScheduledMessages = createAsyncThunk(
  'candidate/fetchCandidateScheduledMessages',
  async ({ candidateId, page, itemsPerPage }, { rejectWithValue }) => {
    try {
      return await getCandidateScheduledMessages({
        candidateId,
        page,
        itemsPerPage,
      });
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const fetchAllTemplates = createAsyncThunk(
  'candidate/fetchAllTemplates',
  async ({ userName, sort, code }, { rejectWithValue }) => {
    try {
      const response = await getMessageTemplates({ userName, code, sort });

      response.userName = userName;
      return response;
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const addCandidate = createAsyncThunk(
  'candidate/add',
  async ({ firstName, lastName, phoneNumber, channel, jobTitle }, { rejectWithValue }) => {
    try {
      return await addCandidateApi({
        firstName,
        lastName,
        phoneNumber,
        channel,
        jobTitle,
      });
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const validateCandidate = createAsyncThunk(
  'candidate/validatePhoneNumber',
  async ({ candidateId }, { rejectWithValue }) => {
    try {
      return await validateCandidateApi({ candidateId });
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const updateCandidate = createAsyncThunk(
  'candidate/update',
  async (
    { candidateId, firstName, lastName, phoneNumber, channel, jobTitle },
    { rejectWithValue },
  ) => {
    try {
      return await updateCandidateApi({
        candidateId,
        firstName,
        lastName,
        phoneNumber,
        channel,
        jobTitle,
      });
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const updateStarredCandidate = createAsyncThunk(
  'candidate/star',
  async ({ candidateId, isStarred }, { rejectWithValue }) => {
    try {
      return await starCandidateApi({ candidateId, isStarred });
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);
export const updateInactiveCandidate = createAsyncThunk(
  'candidate/inactive',
  async ({ candidateId, isInactive }, { rejectWithValue }) => {
    try {
      return await inactiveCandidateApi({ candidateId, isInactive });
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);
export const updateArchiveCandidate = createAsyncThunk(
  'candidate/archive',
  async ({ candidateId, isArchive }, { rejectWithValue }) => {
    try {
      return await archiveCandidateApi({ candidateId, isArchive });
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const resetCandidateSave = createAction('candidate/resetCandidateSave');
export const updateAllActiveCandidate = createAsyncThunk(
  'candidate/activeall',
  async ({ candidateId }, { rejectWithValue }) => {
    try {
      return await updateAllActiveCandidateApi({ candidateId });
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const updateDoNotTextCandidate = createAsyncThunk(
  'candidate/doNotText',
  async ({ candidateId }, { rejectWithValue }) => {
    try {
      return await updateDoNotTextCandidateApi({ candidateId });
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const fetchNumberCandidatesWithTags = createAsyncThunk(
  'candidate/candidateQuery',
  async ({ lastContacted, tagsData }, { rejectWithValue }) => {
    try {
      return await candidateQueryWithTags({ lastContacted, tagsData });
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);
export const candidatesUpload = createAsyncThunk(
  'candidate/candidatesUpload',
  async ({ selectedFile, uid }, { rejectWithValue }) => {
    try {
      return await uploadCandidatesApi({ selectedFile, uid });
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const candidatesImport = createAsyncThunk(
  'candidate/candidatesImport',
  async ({ uploadHeaders, customerId, campaignId, uid, isUserUpload }, { rejectWithValue }) => {
    try {
      return await candidatesFileUploadApi({
        uploadHeaders,
        customerId,
        campaignId,
        uid,
        isUserUpload,
      });
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const removeSelectedCampaign = createAsyncThunk(
  'candidate/removeSelectedCampaign',
  async ({ candidateId, jobIds }, { rejectWithValue }) => {
    try {
      return await removeSelectedCampaignsApi({ candidateId, jobIds });
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const fetchOmniSearch = createAsyncThunk(
  'candidate/omniSearch',
  async ({ search }, { rejectWithValue }) => {
    try {
      return await fetchOmniSearchApi({ search });
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const cancelScheduledMessages = createAsyncThunk(
  'candidate/cancelScheduledMessages',
  async ({ id, customerId }, { rejectWithValue }) => {
    try {
      return await cancelScheduledMessagesApi({ id, customerId });
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const fetchCandidatesInSequence = createAsyncThunk(
  'candidate/fetchCandidatesInSequence',
  async ({ sendTo }, { rejectWithValue }) => {
    try {
      return await fetchCandidatesInSequenceApi({ sendTo });
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);
